import { bool, node, number, object, string } from "prop-types";
import { useMemo, useRef } from "react";

import styled from "styled-components";

import FnrTypography from "FnrTypography";
import { motion } from "framer-motion";

import { addOpacity } from "Utilities/Colors/colorUtils";
import useComputedStyle from "Utilities/Hooks/useComputedStyle";
import useHeight from "Utilities/Hooks/useHeight";
import useWidth from "Utilities/Hooks/useWidth";

const DefaultLabel = ({
  hasValue,
  label,
  left: leftArg,
  children,
  inputRef,
  topAlign,
  ...rest
}) => {
  const labelRef = useRef();
  const inputHeight = useHeight(inputRef);
  const inputPaddingLeft = useComputedStyle(inputRef, "padding-left");
  const inputPaddingTop = useComputedStyle(inputRef, "padding-top");
  const inputBackgroundColor = useComputedStyle(inputRef, "backgroundColor");
  const labelWidth = useWidth(labelRef);
  const left = useMemo(() => {
    if (leftArg) return `${leftArg}px`;
    return inputPaddingLeft;
  }, [inputPaddingLeft, leftArg]);

  const labelAnimate = useMemo(() => {
    if (hasValue)
      return {
        top: "0px",
        transform: "translateY(-60%)",
      };
    if (topAlign) {
      return {
        top: `calc(${
          inputPaddingTop || "12px"
        } + 1px)` /* 12px for a fallback because it's probably right. 1px for the border */,
      };
    }
    if (inputHeight)
      return { top: `${inputHeight / 2}px`, transform: "translateY(-50%)" };
    return { top: `50%`, transform: "translateY(-50%)" };
  }, [hasValue, inputHeight, inputPaddingTop, topAlign]);

  return (
    <>
      <LineHider
        $width={`${labelWidth ? labelWidth + 4 : 0}px`}
        $left={`calc(${left} - 2px)`}
        $borderColor={inputBackgroundColor}
        $show={hasValue}
      />
      {inputRef.current && (
        <LabelPos
          initial={false}
          data-cy="DefaultLabel_LabelPos"
          animate={labelAnimate}
          transition={{ duration: 0.2, type: "spring" }}
          $left={left}
        >
          <FnrTypography
            ref={labelRef}
            fontSize={hasValue ? "sm" : "lg"}
            fontWeight={300}
            color={
              hasValue ? addOpacity("#333333", 0.8) : addOpacity("#333333", 0.4)
            }
            {...rest}
          >
            {label}
          </FnrTypography>
        </LabelPos>
      )}
    </>
  );
};

const LabelPos = styled(motion.div)`
  position: absolute;
  left: ${({ $left }) => $left};
  transition: left 300ms;
  pointer-events: none;
`;

const opacity = ({ $show }) => {
  if ($show)
    return `
    opacity: 1;
    transition: opacity 300ms;
  `;
  return `
    opacity: 0;
    transition: opacity 50ms;
  `;
};

const LineHider = styled.div`
  position: absolute;
  border-bottom: 4px solid;
  left: ${({ $left }) => $left};
  width: ${({ $width }) => $width};
  border-color: ${({ $borderColor }) => $borderColor};
  top: -2px;
  z-index: 0;
  pointer-events: none;
  ${opacity}
`;

DefaultLabel.propTypes = {
  hasValue: bool,
  label: string,
  inputRef: object,
  children: node,
  left: number,
  topAlign: bool,
};

export default DefaultLabel;
